/**
 * AddLinkActionDialog - Dialog para adicionar/editar ação de link
 * 
 * Permite configurar link com título, descrição e imagem de preview
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Link, ExternalLink } from 'lucide-react';

// =====================================================
// INTERFACES
// =====================================================

interface LinkActionData {
  link_url: string;
  title?: string;
  description?: string;
  image?: string;
}

interface AddLinkActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: LinkActionData) => void;
  initialValue?: Record<string, unknown>;
}

// =====================================================
// COMPONENTE
// =====================================================

export function AddLinkActionDialog({
  open,
  onOpenChange,
  onSave,
  initialValue,
}: AddLinkActionDialogProps) {
  const [linkUrl, setLinkUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState('');

  // Reset ao abrir dialog
  useEffect(() => {
    if (open) {
      if (initialValue) {
        setLinkUrl((initialValue.link_url as string) || '');
        setTitle((initialValue.title as string) || '');
        setDescription((initialValue.description as string) || '');
        setImage((initialValue.image as string) || '');
      } else {
        setLinkUrl('');
        setTitle('');
        setDescription('');
        setImage('');
      }
    }
  }, [open, initialValue]);

  // Handler de salvar
  const handleSave = () => {
    if (linkUrl.trim()) {
      onSave({
        link_url: linkUrl.trim(),
        title: title.trim() || undefined,
        description: description.trim() || undefined,
        image: image.trim() || undefined,
      });
    }
  };

  // Validação
  const isValid = linkUrl.trim().length > 0 && linkUrl.startsWith('http');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link className="h-5 w-5" />
            {initialValue ? 'Editar Link' : 'Adicionar Link'}
          </DialogTitle>
          <DialogDescription>
            Configure o link com preview que será enviado.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="link-url">URL do Link *</Label>
            <Input
              id="link-url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://exemplo.com/pagina"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Título (opcional)</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Título do link"
            />
            <p className="text-xs text-muted-foreground">
              Suporta variáveis: <code className="bg-muted px-1 rounded">{'{{contact_name}}'}</code>, <code className="bg-muted px-1 rounded">{'{{contact_first_name}}'}</code>
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descrição (opcional)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descrição do link..."
              rows={2}
              className="resize-none"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="image">URL da Imagem de Preview (opcional)</Label>
            <Input
              id="image"
              value={image}
              onChange={(e) => setImage(e.target.value)}
              placeholder="https://exemplo.com/imagem.jpg"
            />
          </div>

          {/* Preview */}
          {linkUrl && (
            <div className="p-3 border rounded-lg bg-muted/50">
              <div className="flex items-start gap-3">
                {image ? (
                  <img
                    src={image}
                    alt="Preview"
                    className="w-16 h-16 object-cover rounded"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-16 h-16 bg-muted rounded flex items-center justify-center">
                    <ExternalLink className="h-6 w-6 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {title || 'Link Preview'}
                  </p>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {description || linkUrl}
                  </p>
                  <p className="text-xs text-primary truncate mt-1">
                    {linkUrl}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={!isValid}>
            {initialValue ? 'Salvar' : 'Adicionar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
